import { useState } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Phone,
  Mail,
  Send,
  Truck,
  Package,
  CheckCircle,
  Clock,
  FileText,
  Printer,
  Download,
} from "lucide-react";

// Mock data for approved POs ready for vendor order
const approvedPOs = [
  {
    id: "PO-2024-0085",
    department: "Kitchen",
    vendor: {
      name: "Reliance Fresh Wholesale",
      contact: "9826123456",
      email: "orders@reliancefresh.com",
      address: "Industrial Area, Bhopal",
    },
    approvedDate: "2024-12-18",
    totalItems: 12,
    grandTotal: 45680,
    orderStatus: "pending", // pending, ordered, in_transit, delivered
    items: [
      { name: "Basmati Rice", qty: 50, unit: "kg", rate: 85 },
      { name: "Toor Dal 1 Kg", qty: 30, unit: "kg", rate: 140 },
      { name: "Garam Masala 100 Grm", qty: 20, unit: "pcs", rate: 85 },
    ],
  },
  {
    id: "PO-2024-0084",
    department: "Bar",
    vendor: {
      name: "United Breweries",
      contact: "9893456789",
      email: "sales@ub.com",
      address: "MG Road, Bhopal",
    },
    approvedDate: "2024-12-17",
    totalItems: 8,
    grandTotal: 125400,
    orderStatus: "ordered",
    expectedDelivery: "2024-12-20",
    items: [
      { name: "JW Black Label", qty: 12, unit: "bottle", rate: 3200 },
      { name: "Kingfisher Ultra 650 ML", qty: 120, unit: "bottle", rate: 220 },
    ],
  },
  {
    id: "PO-2024-0083",
    department: "Store",
    vendor: {
      name: "Metro Cash & Carry",
      contact: "9827654321",
      email: "b2b@metro.in",
      address: "Hoshangabad Road, Bhopal",
    },
    approvedDate: "2024-12-16",
    totalItems: 15,
    grandTotal: 8950,
    orderStatus: "in_transit",
    expectedDelivery: "2024-12-19",
    items: [
      { name: "Tissue Paper Rolls", qty: 100, unit: "pcs", rate: 35 },
      { name: "Garbage Bags (Large)", qty: 200, unit: "pcs", rate: 12 },
    ],
  },
];

export default function VendorOrders() {
  const [selectedPO, setSelectedPO] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [poList, setPOList] = useState(approvedPOs);
  const [expectedDelivery, setExpectedDelivery] = useState("");

  const handlePlaceOrder = () => {
    if (selectedPO) {
      setPOList(
        poList.map((po) =>
          po.id === selectedPO.id
            ? {
                ...po,
                orderStatus: "ordered",
                expectedDelivery: expectedDelivery,
              }
            : po
        )
      );
      setShowOrderDialog(false);
      setSelectedPO(null);
      setExpectedDelivery("");
    }
  };

  const handleMarkInTransit = (poId) => {
    setPOList(
      poList.map((po) =>
        po.id === poId ? { ...po, orderStatus: "in_transit" } : po
      )
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-300"
          >
            <Clock className="w-3 h-3 mr-1" />
            To Order
          </Badge>
        );
      case "ordered":
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-300"
          >
            <Send className="w-3 h-3 mr-1" />
            Ordered
          </Badge>
        );
      case "in_transit":
        return (
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-300"
          >
            <Truck className="w-3 h-3 mr-1" />
            In Transit
          </Badge>
        );
      case "delivered":
        return (
          <Badge
            variant="outline"
            className="bg-emerald-50 text-emerald-700 border-emerald-300"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Delivered
          </Badge>
        );
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <MainLayout>
      <Header
        title="Vendor Orders"
        subtitle="Manage orders with vendors for approved Purchase Orders"
      />

      <div className="p-6 space-y-6">
        {/* Workflow Indicator */}
        <div className="flex items-center justify-center gap-4 text-sm">
          <span className="px-2 py-1 rounded bg-muted text-muted-foreground">
            1. Create PO
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="px-2 py-1 rounded bg-muted text-muted-foreground">
            2. Admin Approval
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="px-2 py-1 rounded bg-primary/20 text-primary font-medium">
            3. Vendor Order
          </span>
          <span className="text-muted-foreground">→</span>
          <span className="px-2 py-1 rounded bg-muted text-muted-foreground">
            4. Challan Verify (GRN)
          </span>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">To Order</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {poList.filter((p) => p.orderStatus === "pending").length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ordered</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {poList.filter((p) => p.orderStatus === "ordered").length}
                  </p>
                </div>
                <Send className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Transit</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {
                      poList.filter((p) => p.orderStatus === "in_transit")
                        .length
                    }
                  </p>
                </div>
                <Truck className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                  <p className="text-2xl font-bold text-primary">
                    ₹
                    {poList
                      .reduce((sum, p) => sum + p.grandTotal, 0)
                      .toLocaleString()}
                  </p>
                </div>
                <Package className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* PO List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Approved POs - Vendor Order Management</CardTitle>
            <CardDescription>
              Place orders with vendors and track delivery status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Approved Date</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {poList.map((po) => (
                  <TableRow key={po.id}>
                    <TableCell className="font-medium">{po.id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{po.department}</Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{po.vendor.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {po.vendor.contact}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(po.approvedDate).toLocaleDateString("en-IN")}
                    </TableCell>
                    <TableCell className="text-center">
                      {po.totalItems}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ₹{po.grandTotal.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {getStatusBadge(po.orderStatus)}
                        {po.expectedDelivery && (
                          <p className="text-xs text-muted-foreground">
                            Expected:{" "}
                            {new Date(po.expectedDelivery).toLocaleDateString(
                              "en-IN"
                            )}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedPO(po)}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {po.orderStatus === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPO(po);
                              setShowOrderDialog(true);
                            }}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Order
                          </Button>
                        )}
                        {po.orderStatus === "ordered" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-purple-300 text-purple-600 hover:bg-purple-50"
                            onClick={() => handleMarkInTransit(po.id)}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            In Transit
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* PO Detail Dialog */}
        <Dialog
          open={!!selectedPO && !showOrderDialog}
          onOpenChange={() => setSelectedPO(null)}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selectedPO?.id} - Vendor Order Details
              </DialogTitle>
            </DialogHeader>
            {selectedPO && (
              <div className="space-y-4">
                {/* Vendor Info */}
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Vendor</p>
                        <p className="font-semibold">
                          {selectedPO.vendor.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedPO.vendor.address}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedPO.vendor.contact}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedPO.vendor.email}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Items */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPO.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            {item.name}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.qty}
                          </TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell className="text-right">
                            ₹{item.rate}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            ₹{(item.qty * item.rate * 1.05).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div>
                    <span className="text-muted-foreground">Status: </span>
                    {getStatusBadge(selectedPO.orderStatus)}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Grand Total</p>
                    <p className="text-2xl font-bold text-primary">
                      ₹{selectedPO.grandTotal.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline">
                <Printer className="w-4 h-4 mr-1" />
                Print PO
              </Button>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-1" />
                Download PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Place Order Dialog */}
        <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Place Order with Vendor</DialogTitle>
              <DialogDescription>
                Confirm order placement for {selectedPO?.id} with{" "}
                {selectedPO?.vendor.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg space-y-2">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Vendor:</strong> {selectedPO?.vendor.name}
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Contact:</strong> {selectedPO?.vendor.contact}
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Order Value:</strong> ₹
                  {selectedPO?.grandTotal.toLocaleString()}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Expected Delivery Date
                </label>
                <Input
                  type="date"
                  value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowOrderDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={handlePlaceOrder} disabled={!expectedDelivery}>
                <Send className="w-4 h-4 mr-1" />
                Confirm & Place Order
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
